import { AboutTransportSection } from "@/components/sections/section-about-transporte";
import { Helmet } from "react-helmet-async";

import { HeroSectionCity } from "@/components/heros/section-hero-city";
import { AboutSection } from "@/components/sections/section-about";
import { BannerSection } from "@/components/sections/section-banner";
import { BlogSection } from "@/components/sections/section-blog";
import { CertificationSection } from "@/components/sections/section-certification";
import { CidadesAtendidasSection } from "@/components/sections/section-cities";
import { ColetaPersonalizadaSection } from "@/components/sections/section-coleta-personalizada";
import { CidadesDistanciaSection } from "@/components/sections/section-distance";
import { JourneySection } from "@/components/sections/section-journey";
import { ModalidadeTransporteSection } from "@/components/sections/section-modalidade-transporte";
import { StatsSectionClear } from "@/components/sections/section-stats-clear";
import { TestimonialSection } from "@/components/sections/section-testimonial";
import { TransportSection } from "@/components/sections/section-transport";
import { TransportPriceSection } from "@/components/sections/section-transport-price";
import { WhoSection } from "@/components/sections/section-who";
import { WhySection } from "@/components/sections/section-why-section";
import { useUtmParameters } from "@/hooks/useUtmParameters";
import { precosOrigemDestino } from "@/services/pagesWeb";
import { PrecoOrigemDestino } from "@/types/Endereco";
import { getCityStateBySlug } from "@/utils/cityState";
import { regions } from "@/utils/locales";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type CityPageProps = { slug: string };

const CityPage = ({ slug }: CityPageProps) => {
  const BASE_URL = import.meta.env.VITE_APP_URL;

  useUtmParameters();

  const navigate = useNavigate();
  const [transportRoutes, setTransportRoutes] = useState<PrecoOrigemDestino[]>(
    [],
  );

  const result = getCityStateBySlug(slug || "");
  const city = result?.city;
  const state = result?.state;
  const codeState = result?.codeState;

  useEffect(() => {
    if (result === null) navigate("/transporte-de-veiculos");
  }, [result, navigate]);

  useEffect(() => {
    const fetchPrecos = async () => {
      if (!city || !state) return;

      try {
        const cidadeAtual = regions
          .flatMap((region) => region.cities)
          .find(
            (cityItem) =>
              cityItem.name.toLowerCase() === city.toLowerCase() &&
              cityItem.state.toLowerCase() === state.toLowerCase(),
          );

        if (!cidadeAtual) return;

        const precos = await precosOrigemDestino(cidadeAtual.id);
        setTransportRoutes(precos.slice(0, 5));
      } catch (err) {
        console.error("Erro ao buscar preços:", err);
      }
    };

    fetchPrecos();
  }, [city, state]);

  const OG_IMAGE = `${BASE_URL}/og/webclip.png`;
  const CANONICAL = slug
    ? `${BASE_URL}/transporte-de-veiculos-${slug}`
    : `${BASE_URL}/transporte-de-veiculos`;

  return (
    <div className="bg-n-bg-1">
      <Helmet>
        <title>
          {city && state
            ? `Transporte de Veículos em ${city}`
            : "Transporte de Veículos"}
        </title>

        <meta
          name="description"
          content={
            city && state
              ? `A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.`
              : "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável."
          }
        />

        <link rel="canonical" href={CANONICAL} />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="pt_BR" />
        <meta
          property="og:title"
          content={
            city && state
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos"
          }
        />
        <meta
          property="og:description"
          content={
            city && state
              ? `A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.`
              : "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável."
          }
        />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta
          property="og:image:alt"
          content="Transporte de veículos - Nacional GO"
        />
        <meta property="og:site_name" content="Nacional GO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={
            city && state
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos"
          }
        />
        <meta
          name="twitter:description"
          content={
            city && state
              ? `A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.`
              : "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável."
          }
        />
        <meta name="twitter:image" content={OG_IMAGE} />

        {/* Schema.org Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Nacional GO",
            telephone: "0800 878 0708",

            address: {
              "@type": "PostalAddress",
              streetAddress:
                "Rodovia José Carlos Daux, 4150\nPrimavera Office, Sala 102\nSaco Grande, Florianópolis/SC",
              addressLocality: "Florianópolis",
              addressRegion: "SC",
              postalCode: "88.032-005",
            },
          })}
        </script>

        {/* Schema.org WebPage */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: city
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos",
            description:
              "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.",
            inLanguage: "pt-BR",
            url: CANONICAL,
          })}
        </script>

        {/* Schema.org Service */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: city
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos",
            serviceType: city
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos",
            description:
              "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.",
            areaServed: city || "Brasil",
            provider: { "@type": "Organization", name: "Nacional GO" },
          })}
        </script>

        {/* Schema.org ItemList - Soluções de transporte */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Seja qual for a necessidade, temos a solução ideal para você.",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Cegonha Convencional",
              },
              { "@type": "ListItem", position: 2, name: "Cegonha Expresso" },
              {
                "@type": "ListItem",
                position: 3,
                name: "Guincho aberto ou fechado",
              },
            ],
          })}
        </script>

        {/* Schema.org ItemList - Casos de uso */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Especialistas em transporte de carros com soluções pensadas para você e seu negócio.",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Vai mudar de cidade ou de emprego?",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Comprou ou vendeu um carro? A gente cuida do transporte.",
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Para quem entende o que tem na garagem",
              },
              {
                "@type": "ListItem",
                position: 4,
                name: "Sua operação não pode parar — e, com a gente, não para!",
              },
            ],
          })}
        </script>

        {/* Schema.org Service com Offers */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: city
              ? `Transporte de Veículos em ${city}`
              : "Transporte de Veículos",
            description:
              "A Nacional GO é especialista no transporte de carros, motos e outros veículos, oferecendo um serviço seguro, ágil e confiável.",
            areaServed: city || "Brasil",
            provider: { "@type": "Organization", name: "Nacional GO" },
            offers:
              transportRoutes.length > 0
                ? transportRoutes.map((route) => ({
                    "@type": "Offer",
                    name: `${route.origem} → ${route.destino}`,
                    price: route.preco_medio.toString(),
                    priceCurrency: "BRL",
                  }))
                : [
                    {
                      "@type": "Offer",
                      name: "Nenhuma oferta disponível",
                      price: "0",
                      priceCurrency: "BRL",
                    },
                  ],
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <HeroSectionCity cityName={city} />

      {/* About Transport Section */}
      <AboutTransportSection cityName={city} stateName={state} />

      <ModalidadeTransporteSection />

      {/* Seção do tipos de transporte */}
      <TransportSection />

      {/* Jornada do usuário */}
      <JourneySection stateName={state} />

      {/* Custo do transporte */}
      <TransportPriceSection cityName={city} stateName={state} />

      {/* Cidades distâncias */}
      <CidadesDistanciaSection stateName={state} cityName={city} />

      <ColetaPersonalizadaSection />

      {/* Cidades atendidas */}
      <CidadesAtendidasSection
        stateName={state}
        codeState={codeState}
        cityName={city}
      />

      {/* for who section */}
      <WhoSection />

      {/* Why nacional go */}
      <WhySection />

      {/* Stats Section */}
      <StatsSectionClear />

      {/* Testimonial section */}
      <TestimonialSection />

      {/* Certification section */}
      <CertificationSection />

      <BlogSection />

      {/* About */}
      <AboutSection />

      {/* Banner de cotação */}
      <BannerSection title="Faça agora uma cotação e agende o transporte do seu veículo !" />
    </div>
  );
};

export default CityPage;
